import { type ChangeEvent, type FC, useCallback, useEffect } from "react";
import { type LinearSubstep, type SubstepTile } from "../../models";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../../../redux/store";
import SubstepFrame from "../SubstepFrame";
import LinearTile from "../LinearTile";
import { defaultMemoize } from "reselect";
import { type CustomDispatch } from "../../../../redux/types";
import { Col, Row } from "../../../../common/components/Layout";
import { updateCsticValuesFromTile, updateCsticValuesOld } from "../../redux/actions";
import { getKmats } from "../../redux/selectors";
import LinearInfo from "../LinearInfo";
import { SubstepType } from "../../dtos";
import LanguageAwareExternalContent from "../LanguageAwareExternalContent";
import { useExternalContent } from "../../hooks/useExternalContent";
import { SimpleSelect } from "../../../../common/components/Select/SimpleSelect";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import { formatCsticsToSet } from "../../utils";
import { type DropdownEntry } from "~/common/types";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { useTranslation } from "react-i18next";
import { isTileEnabled, isTileStandard } from "~/Configurators/common/tileUtils";
import { type ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { useConfiguratorType } from "~/Configurators/common/useConfiguratorType";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const LubricationSubstep: FC<Props> = ({ def, ...props }) => {
  const { t } = useTranslation();
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const [tileData, dropdownData] = getTileData(def, dispatch);
  const kmats = useSelector(getKmats);
  const configuratorType = useConfiguratorType()!;

  const colSize = (Math.floor(12 / def.tilesPerRow) || 1) as 12 | 11 | 10 | 9 | 8 | 7 | 6 | 5 | 4 | 3 | 2 | 1;
  const lastIndex = tileData.length - 1;

  const selectedTileIndex = def.selectedTile !== null ? def.selectedTile - tileData.length : -1;
  const ddSelectedValue = dropdownData[selectedTileIndex] || undefined;

  const pdfLink = useSelector(getPdfLink);
  const debugLanguage = isDebugLanguageEnabled();

  const { state: descriptionState } = useExternalContent(def.description);
  useEffect(() => {
    const el = document.querySelector('a[id^="https://"]');
    if (el && pdfLink) {
      const url = el.getAttribute("id")!;
      el.setAttribute("href", url);
      el.setAttribute("target", "_blank");
    }
  }, [descriptionState, pdfLink]);

  const { val: isOpen, setTrue: openDropdown, setFalse: closeDropdown } = useBooleanState(false);

  const onDropdownClick2 = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const clickedTile = dropdownData[+e.target.value];
      clickedTile.onClick();
    },
    [dropdownData],
  );

  const showDropdownOnLastTile = def.extraConfig?.hasDropdown;

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={def.selectedTile !== null}
      showError={def.isInvalid}
      errorMessage={def.isInvalid ? t("linear.step.select.option") : ""}
      {...props}
    >
      <div className={"w-100"}>
        <Row className={"d-flex"}>
          {tileData.map((data, index) => (
            <Col size={colSize} className={"mb-30"} key={index}>
              {index === lastIndex && showDropdownOnLastTile ? (
                <LinearTile
                  imageUrl={data.imageUrl}
                  title={debugLanguage ? formatCsticsToSet(data.cstics) : data.name}
                  description={data.description}
                  info={data.info}
                  isPicked={!!def.selectedTile && def.selectedTile >= tileData.length}
                  isDisabled={!isTileEnabled(data, kmats)}
                  isStandard={isTileStandard(data, kmats)}
                  onClickHandler={() => {
                    const isSelected = !!def.selectedTile && def.selectedTile >= tileData.length;
                    if (!isSelected) {
                      data.onClick();
                    }
                    openDropdown();
                  }}
                >
                  <SimpleSelect
                    values={makeDropdownEntries2(dropdownData, configuratorType)}
                    onClose={(e) => {
                      closeDropdown();
                      e.stopPropagation();
                    }}
                    value={dropdownData.indexOf(ddSelectedValue)}
                    onChange={onDropdownClick2}
                    open={isOpen}
                  />
                </LinearTile>
              ) : (
                <LinearTile
                  onClickHandler={data.onClick}
                  imageUrl={data.imageUrl}
                  title={debugLanguage ? formatCsticsToSet(data.cstics) : data.name}
                  description={data.description}
                  info={data.info}
                  isPicked={data.tileIndex === def.selectedTile}
                  isDisabled={!isTileEnabled(data, kmats)}
                  isStandard={isTileStandard(data, kmats)}
                />
              )}
            </Col>
          ))}
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default LubricationSubstep;

type Tile = SubstepTile & {
  onClick: () => void;
  tileIndex: number;
};

const getTileData = defaultMemoize((stepElement: LinearSubstep, dispatch: CustomDispatch<RootState>) => {
  const allTiles: Tile[] = stepElement.tiles.map((tile, index) => ({
    ...tile,
    onClick:
      index < stepElement.tiles.length - 1
        ? () => dispatch(updateCsticValuesFromTile(stepElement.type, index, tile))
        : () => dispatch(updateCsticValuesOld(tile.cstics, SubstepType.Lubrication)),
    tileIndex: index,
  }));

  let allDropdownValues: Tile[] = [];
  if (stepElement.groupedTiles[0]) {
    allDropdownValues = stepElement.groupedTiles[0].tiles.map((tile, index) => ({
      ...tile,
      onClick: () => dispatch(updateCsticValuesFromTile(stepElement.type, index + stepElement.tiles.length, tile)),
      tileIndex: index,
    }));
  }

  return [allTiles, allDropdownValues];
});

const makeDropdownEntries2 = defaultMemoize(
  (tiles: Tile[], configuratorType: ConfiguratorType): DropdownEntry<number>[] => {
    return tiles.map((tile, index) => ({
      label: isDebugLanguageEnabled() ? (
        formatCsticsToSet(tile.cstics)
      ) : (
        <div className={"d-flex flex-justify-between"}>
          <LanguageAwareExternalContent
            content={tile.name}
            loadedClassname={"scha__lc__external-label scha__lc__grease-dropdown"}
          />

          <LinearInfo info={tile.info} popupClassName={"scha__lc__external-content--shaft"} />
        </div>
      ),
      value: index,
    }));
  },
);

const getPdfLink = (store: RootState) => store.linearConfigurator.step5PdfLink;
